import { css } from '@emotion/react';
import { cn } from '@anytag/shared-utils';
import { THEME } from '@anytag/shared-constants';
import { TextCutter } from '@anytag/shared-atoms';
import { InstagramCompareFollowers } from '@anytag/graphql';
import { GenderChart, AgeGenderChart, InfluencerAvatar } from '@anytag/shared-molecules';
import { AnalyticsInstagramComparableAccountType } from '../types';

interface TemplateProps {
  itemsPerRow?: number;
  data: InstagramCompareFollowers;
  comparableAccounts: AnalyticsInstagramComparableAccountType[];
}

export const Template = ({ data, itemsPerRow = 2, comparableAccounts }: TemplateProps) => {
  const composeData = [
    data.mainAccount,
    ...(data.compareAccounts.filter((account) => account.id !== data.mainAccount.id) || [])
  ];

  return (
    <div className="flex flex-wrap">
      {comparableAccounts.map(({ id, username, avatarUrl }) => {
        const audienceData = composeData.find((item) => item.id === id);
        const noGenderData = !(
          audienceData?.followerGenderRate.femaleRate || audienceData?.followerGenderRate.maleRate
        );

        return (
          <div key={id} css={styles.item(itemsPerRow, comparableAccounts.length)}>
            <div className="mb-3 flex min-h-29 items-center gap-4">
              <div className="flex w-[30%] items-center">
                <InfluencerAvatar size="40px" src={avatarUrl} className="rounded-full" />
                <TextCutter text={username} className="ml-2 text-base" />
              </div>

              <GenderChart
                male={audienceData?.followerGenderRate.maleRate}
                female={audienceData?.followerGenderRate.femaleRate}
                className={cn('w-[70%]', { 'min-h-[inherit] scale-[0.5]': noGenderData })}
              />
            </div>

            <AgeGenderChart
              maleRate={audienceData?.followerAgeRate.maleRate}
              femaleRate={audienceData?.followerAgeRate.femaleRate}
              ageGroup={
                audienceData?.followerAgeRate.ageGroup?.map((item) =>
                  item.start === 65 ? '65+' : `${item.start}-${item.end}`
                ) || []
              }
            />
          </div>
        );
      })}
    </div>
  );
};
const styles = {
  item: (itemsPerRow: number, totalRecords: number) =>
    css({
      '--size': `calc(100% / ${itemsPerRow})`,

      padding: '16px',
      flex: 'var(--size)',
      boxSizing: 'border-box',
      maxWidth: 'var(--size)',
      borderRight: THEME.border.base,
      borderBottom: THEME.border.base,

      // Remove last element in row and last row border
      [`&:nth-of-type(${itemsPerRow}n + ${itemsPerRow})`]: { borderRight: 'none' },
      [`&:nth-last-of-type(-n + ${totalRecords % itemsPerRow || itemsPerRow})`]: { borderBottom: 'none' }
    })
};
